import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { LogOut, Plus, Pencil, Trash2, Lock, Upload, Image as ImageIcon, Loader2, Palette, GripVertical, Sparkles, Undo2, ChevronUp, ChevronDown, Eye, EyeOff } from "lucide-react";
import {
  DndContext,
  closestCenter,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api, formatApiErrorDetail, uploadImage, resolveImg } from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import AdminAI from "../components/site/AdminAI";
import ImageCropperModal from "../components/ImageCropperModal";
import { StatCard, ActivityLineChart, TopBarChart, RangeToggle } from "../components/AnalyticsCharts";
import UnifiedContent from "./admin/UnifiedContent";
import Activity from "./admin/Activity";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";

const LIGHT_THEME = {
  "--ink": "#111111",
  "--ink-soft": "#666666",
  "--accent": "#0f3d32",
  "--accent-light": "#eaf0ee",
  "--hairline": "rgba(17,17,17,0.08)",
  background: "#f4f3f1",
  color: "#111111",
  fontFamily: "'Inter', system-ui, sans-serif",
  minHeight: "100vh",
};

const CARD_FIELDS = [
  ["category", "Category"],
  ["bank_name", "Bank name"],
  ["card_name", "Card name"],
  ["benefit", "One-line benefit"],
  ["label", "Label (optional)"],
  ["cta_text", "Button text"],
  ["cta_url", "Apply / Learn URL"],
  ["fee_note", "Fee note (optional)"],
  ["icon", "Icon key (credit-card, plane, wallet, fuel, gem, crown, smartphone, briefcase, badge-check)"],
  ["image_url", "Card image / logo"],
];
const PRODUCT_FIELDS = [
  ["collection_key", "Collection (wellness | lifestyle)"],
  ["brand", "Brand"],
  ["name", "Product name"],
  ["recommendation", "One-line recommendation"],
  ["image_url", "Product image"],
  ["discount_label", "Discount label"],
  ["shop_url", "Shop URL"],
];

function ImageField({ k, label, value, onChange }) {
  const [busy, setBusy] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const inputRef = useRef(null);
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setCropSrc(URL.createObjectURL(f));
    e.target.value = "";
  };
  const editExisting = async () => {
    if (!value) return;
    try {
      const res = await fetch(resolveImg(value), { credentials: "omit" });
      if (!res.ok) throw new Error("fetch failed");
      const blob = await res.blob();
      setCropSrc(URL.createObjectURL(blob));
    } catch { toast.error("Couldn't load the image to re-crop. Upload a new one instead."); }
  };
  const onCropped = async (blob) => {
    setCropSrc(null);
    setBusy(true);
    try {
      const file = new File([blob], `crop-${Date.now()}.jpg`, { type: blob.type || "image/jpeg" });
      const url = await uploadImage(file);
      onChange(k, url);
      toast.success("Image updated");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={editExisting}
          disabled={!value || busy}
          data-testid={`edit-crop-${k}`}
          title={value ? "Click to re-crop this image" : "No image yet"}
          className="group relative flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-black/10 bg-black/5 disabled:cursor-default"
        >
          {value ? (
            <>
              <img src={resolveImg(value)} alt="" className="h-full w-full object-cover" />
              <span className="absolute inset-0 hidden items-center justify-center bg-black/50 text-[10px] font-bold text-white group-hover:flex">Re-crop</span>
            </>
          ) : (
            <ImageIcon size={18} className="text-black/30" />
          )}
        </button>
        <button
          type="button"
          data-testid={`upload-${k}`}
          onClick={() => inputRef.current?.click()}
          className="inline-flex items-center gap-1.5 rounded-full border border-black/15 px-3.5 py-2 text-xs font-semibold"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {value ? "Replace" : "Upload"} &amp; crop
        </button>
        <input ref={inputRef} type="file" accept="image/*" hidden onChange={onFile} />
      </div>
      <Input placeholder="or paste an image URL" value={value ?? ""} onChange={(e) => onChange(k, e.target.value)} />
      {cropSrc && (
        <ImageCropperModal
          src={cropSrc}
          round={k === "avatar_url"}
          defaultAspect={1}
          onCancel={() => setCropSrc(null)}
          onCropped={onCropped}
        />
      )}
    </div>
  );
}

function Field({ k, label, value, onChange }) {
  if (k === "image_url" || k === "avatar_url") {
    return <ImageField k={k} label={label} value={value} onChange={onChange} />;
  }
  const long = ["benefit", "recommendation"].includes(k);
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      {long ? (
        <Textarea value={value ?? ""} onChange={(e) => onChange(k, e.target.value)} rows={2} />
      ) : (
        <Input value={value ?? ""} onChange={(e) => onChange(k, e.target.value)} />
      )}
    </div>
  );
}

function SortableItem({ it, columns, onEdit, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: it.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 20 : undefined,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 rounded-xl border border-[var(--hairline)] bg-white p-3"
    >
      <button
        {...attributes}
        {...listeners}
        aria-label="Drag to reorder"
        data-testid={`drag-${it.id}`}
        className="cursor-grab touch-none rounded-md p-1 text-black/30 hover:text-black/60 active:cursor-grabbing"
      >
        <GripVertical size={18} />
      </button>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">{it[columns[0]]}</p>
        <p className="truncate text-xs text-[var(--ink-soft)]">
          {columns.slice(1).map((c) => it[c]).filter(Boolean).join(" · ")}
        </p>
      </div>
      <button onClick={() => onEdit(it)} className="shrink-0 rounded-lg p-2 hover:bg-black/5">
        <Pencil size={15} />
      </button>
      <button onClick={() => onRemove(it)} className="shrink-0 rounded-lg p-2 text-red-600 hover:bg-red-50">
        <Trash2 size={15} />
      </button>
    </div>
  );
}

function EntityManager({ title, endpoint, fields, columns }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({});
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const load = useCallback(async () => {
    const res = await api.get(endpoint);
    setItems(res.data);
  }, [endpoint]);

  useEffect(() => {
    load();
  }, [load]);

  const startNew = () => {
    const base = {};
    fields.forEach(([k]) => (base[k] = ""));
    base.order = items.length + 1;
    base.active = true;
    setDraft(base);
    setOpen(true);
  };
  const startEdit = (it) => {
    setDraft({ ...it });
    setOpen(true);
  };
  const change = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const save = async () => {
    const payload = { ...draft };
    payload.order = parseInt(payload.order, 10) || 0;
    delete payload._id;
    try {
      if (draft.id) {
        const { id, ...body } = payload;
        await api.put(`${endpoint}/${draft.id}`, body);
      } else {
        await api.post(endpoint, payload);
      }
      toast.success("Saved");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete "${it[columns[0]]}"?`)) return;
    await api.delete(`${endpoint}/${it.id}`);
    toast.success("Deleted");
    load();
  };

  const onDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    const next = arrayMove(items, oldIndex, newIndex);
    setItems(next);
    try {
      await api.post(`${endpoint}/reorder`, { ids: next.map((i) => i.id) });
      toast.success("Order saved");
    } catch (e) {
      toast.error("Could not save order");
      load();
    }
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">{title}</h2>
          <p className="text-xs text-[var(--ink-soft)]">Drag the handle to reorder.</p>
        </div>
        <button
          onClick={startNew}
          data-testid={`add-${title}`}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[var(--accent)] px-4 py-2 text-sm font-semibold text-white"
        >
          <Plus size={15} /> Add
        </button>
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {items.map((it) => (
              <SortableItem key={it.id} it={it} columns={columns} onEdit={startEdit} onRemove={remove} />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto bg-white text-[#111111]" style={{ "--ink": "#111111", "--ink-soft": "#666666", "--accent": "#0f3d32", "--accent-light": "#eaf0ee", "--hairline": "rgba(17,17,17,0.08)", color: "#111111" }}>
          <DialogHeader>
            <DialogTitle>{draft.id ? "Edit" : "Add"} item</DialogTitle>
            <DialogDescription>Fill in the details below, then Save.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {fields.map(([k, label]) => (
              <Field key={k} k={k} label={label} value={draft[k]} onChange={change} />
            ))}
            <div className="flex items-center gap-2 pt-1">
              <Switch checked={draft.active ?? true} onCheckedChange={(v) => change("active", v)} />
              <Label className="text-xs">Active (visible on site)</Label>
            </div>
          </div>
          <DialogFooter>
            <button onClick={save} data-testid="save-item" className="w-full rounded-full bg-[var(--accent)] py-3 text-sm font-semibold text-white">
              Save
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Leads() {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/admin/consultations").then((r) => setRows(r.data));
  }, []);
  if (!rows.length) return <p className="text-sm text-[var(--ink-soft)]">No consultation requests yet.</p>;
  return (
    <div className="space-y-2">
      {rows.map((r) => (
        <div key={r.id} className="rounded-xl border border-[var(--hairline)] bg-white p-3">
          <div className="flex justify-between">
            <p className="text-sm font-semibold">{r.name}</p>
            <p className="text-xs text-[var(--ink-soft)]">{(r.created_at || "").slice(0, 10)}</p>
          </div>
          <p className="text-xs text-[var(--ink-soft)]">{r.email} · {r.phone || "—"} · {r.income || "—"}</p>
          {r.message && <p className="mt-1 text-xs">{r.message}</p>}
        </div>
      ))}
    </div>
  );
}

function Analytics() {
  const [a, setA] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(`/admin/analytics?days=${days}`).then((r) => setA(r.data)).finally(() => setLoading(false));
  }, [days]);
  if (!a && loading) return <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-[var(--ink-soft)]" /></div>;
  if (!a) return null;
  const byType = (a.by_type || []).map((r) => ({ name: r.type.replace(/_/g, " "), count: r.count }));
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Analytics</h2>
          <p className="text-xs text-[var(--ink-soft)]">Clicks, leads and traffic on your public page.</p>
        </div>
        <RangeToggle value={days} onChange={setDays} testid="analytics-range" />
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard testid="stat-clicks" label="Total clicks" value={a.total_clicks} tint="indigo" />
        <StatCard testid="stat-leads" label="Leads" value={a.total_leads} tint="emerald" />
        <StatCard testid="stat-cards" label="Card clicks" value={(a.by_type || []).find((t) => t.type === "credit_card")?.count || 0} tint="amber" />
        <StatCard testid="stat-products" label="Product clicks" value={(a.by_type || []).find((t) => t.type === "product")?.count || 0} tint="rose" />
      </div>

      <ActivityLineChart
        series={a.series || []}
        keys={[
          { key: "clicks", label: "Clicks", color: "#6366f1" },
          { key: "leads", label: "Leads", color: "#10b981" },
        ]}
        testid="chart-activity"
      />

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <TopBarChart data={a.top_clicks || []} nameKey="name" valueKey="count" title="Top clicked" color="#0f3d32" testid="chart-top-clicks" />
        <TopBarChart data={byType} nameKey="name" valueKey="count" title="Clicks by type" color="#6366f1" testid="chart-by-type" />
      </div>
    </div>
  );
}

function Sections() {
  const [tiles, setTiles] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/content").then((r) => setTiles(r.data.settings?.tiles || []));
  }, []);
  if (!tiles) return null;

  const update = (i, patch) => setTiles((prev) => prev.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));
  const move = (i, dir) => setTiles((prev) => {
    const j = i + dir;
    if (j < 0 || j >= prev.length) return prev;
    const next = [...prev];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  const removeAt = (i) => setTiles((prev) => prev.filter((_, idx) => idx !== i));
  const addCustom = () => setTiles((prev) => [
    ...prev,
    {
      id: `custom_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
      type: "custom",
      title: "New Section",
      subtitle: "",
      visible: true,
      items: [],
    },
  ]);

  const setItems = (i, next) => update(i, { items: next });
  const addItem = (i) => {
    const cur = tiles[i].items || [];
    setItems(i, [...cur, { _key: `n_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`, label: "", url: "", description: "" }]);
  };
  const updateItem = (i, j, patch) => {
    const cur = tiles[i].items || [];
    setItems(i, cur.map((it, idx) => (idx === j ? { ...it, ...patch } : it)));
  };
  const removeItem = (i, j) => {
    const cur = tiles[i].items || [];
    setItems(i, cur.filter((_, idx) => idx !== j));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { tiles });
      toast.success("Sections saved");
      // Refresh from server to reflect server-side normalisation.
      const r = await api.get("/content");
      setTiles(r.data.settings?.tiles || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold">Home page sections</h2>
          <p className="text-xs text-[var(--ink-soft)]">Rename, hide, and reorder the collapsible tiles on your public page. The content inside each built-in tile (cards, products, consultations) is still managed on the Content tab. Add your own custom sections below.</p>
        </div>
        <button
          onClick={addCustom}
          data-testid="section-add-custom"
          className="inline-flex items-center gap-1.5 self-start whitespace-nowrap rounded-full bg-[var(--accent)] px-4 py-2 text-xs font-semibold text-white sm:self-auto"
        >
          <Plus size={14} /> Add section
        </button>
      </div>
      <div className="space-y-3">
        {tiles.map((t, i) => (
          <div key={t.id} data-testid={`section-row-${t.id}`} className="rounded-2xl border border-[var(--hairline)] bg-white p-4">
            <div className="mb-3 flex items-center gap-2">
              <span className="rounded-full bg-black/5 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-black/60">{String(i + 1).padStart(2, "0")}</span>
              {t.type === "custom" ? (
                <span className="rounded-full bg-fuchsia-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-fuchsia-700">Custom</span>
              ) : (
                <span className="text-[11px] font-mono text-black/40">{t.id}</span>
              )}
              <div className="ml-auto flex items-center gap-1">
                <button onClick={() => move(i, -1)} disabled={i === 0} data-testid={`section-up-${t.id}`} className="rounded-lg p-1.5 text-black/60 hover:bg-black/5 disabled:opacity-30"><ChevronUp size={15} /></button>
                <button onClick={() => move(i, 1)} disabled={i === tiles.length - 1} data-testid={`section-down-${t.id}`} className="rounded-lg p-1.5 text-black/60 hover:bg-black/5 disabled:opacity-30"><ChevronDown size={15} /></button>
                <button
                  onClick={() => update(i, { visible: !t.visible })}
                  data-testid={`section-visible-${t.id}`}
                  className={`rounded-lg p-1.5 ${t.visible ? "text-emerald-600 hover:bg-emerald-50" : "text-black/30 hover:bg-black/5"}`}
                  title={t.visible ? "Visible — click to hide" : "Hidden — click to show"}
                >
                  {t.visible ? <Eye size={15} /> : <EyeOff size={15} />}
                </button>
                {t.type === "custom" && (
                  <button
                    onClick={() => { if (window.confirm("Delete this section?")) removeAt(i); }}
                    data-testid={`section-delete-${t.id}`}
                    className="rounded-lg p-1.5 text-red-600 hover:bg-red-50"
                    title="Delete section"
                  >
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <div className="space-y-1">
                <Label className="text-xs">Title</Label>
                <Input value={t.title} onChange={(e) => update(i, { title: e.target.value })} data-testid={`section-title-${t.id}`} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Subtitle</Label>
                <Input value={t.subtitle || ""} onChange={(e) => update(i, { subtitle: e.target.value })} data-testid={`section-subtitle-${t.id}`} />
              </div>
            </div>

            {t.type === "custom" && (
              <div className="mt-4 rounded-xl bg-black/[0.02] p-3">
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-xs font-bold uppercase tracking-wide text-black/60">Items</p>
                  <button onClick={() => addItem(i)} data-testid={`section-add-item-${t.id}`} className="inline-flex items-center gap-1 rounded-full border border-black/15 px-2.5 py-1 text-[11px] font-semibold hover:bg-black/5">
                    <Plus size={12} /> Add item
                  </button>
                </div>
                {(t.items || []).length === 0 ? (
                  <p className="py-2 text-center text-xs text-black/40">No items yet — add the links or resources you want to show inside this section.</p>
                ) : (
                  <div className="space-y-2">
                    {(t.items || []).map((it, j) => (
                      <div key={j} data-testid={`section-item-${t.id}-${j}`} className="rounded-lg border border-black/10 bg-white p-2.5">
                        <div className="flex gap-2">
                          <div className="flex-1 space-y-1.5">
                            <Input placeholder="Label (e.g. Latest newsletter)" value={it.label || ""} onChange={(e) => updateItem(i, j, { label: e.target.value })} data-testid={`section-item-label-${t.id}-${j}`} />
                            <Input placeholder="URL (https://…)" value={it.url || ""} onChange={(e) => updateItem(i, j, { url: e.target.value })} data-testid={`section-item-url-${t.id}-${j}`} />
                            <Input placeholder="Short description (optional)" value={it.description || ""} onChange={(e) => updateItem(i, j, { description: e.target.value })} data-testid={`section-item-desc-${t.id}-${j}`} />
                          </div>
                          <button
                            onClick={() => removeItem(i, j)}
                            data-testid={`section-item-delete-${t.id}-${j}`}
                            className="self-start rounded-lg p-2 text-red-600 hover:bg-red-50"
                            title="Remove item"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="pt-2">
        <button onClick={save} disabled={saving} data-testid="sections-save" className="inline-flex items-center gap-2 rounded-full bg-[var(--accent)] px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60">
          {saving ? <Loader2 size={14} className="animate-spin" /> : null}
          {saving ? "Saving…" : "Save sections"}
        </button>
      </div>
    </div>
  );
}

function Settings() {
  const [s, setS] = useState(null);
  const [grabbing, setGrabbing] = useState(false);
  useEffect(() => {
    api.get("/content").then((r) => setS({
      name: "", creator_name: "", bio: "", avatar_url: "", email: "",
      instagram_url: "", youtube_url: "", linkedin_url: "", ga_measurement_id: "", meta_pixel_id: "",
      linkit_branding: {},
      ...r.data.settings,
    }));
  }, []);
  if (!s) return null;
  const fields = [
    "name", "creator_name", "bio", "avatar_url", "email",
    "instagram_url", "youtube_url", "linkedin_url", "ga_measurement_id", "meta_pixel_id",
  ];
  const brand = s.linkit_branding || {};
  const setBrand = (k, v) => setS((p) => ({ ...p, linkit_branding: { ...(p.linkit_branding || {}), [k]: v } }));
  const brandFields = [
    { k: "founder_name", label: "Founder name" },
    { k: "founder_handle", label: "Founder handle (e.g. ThatCreditCardGuy)" },
    { k: "founder_bio", label: "Founder story (shown on About + landing)" },
    { k: "founder_avatar_url", label: "Founder photo URL (leave empty to use site avatar)" },
    { k: "founder_social_url", label: "Founder social URL" },
    { k: "contact_email", label: "Public contact email (shown on Contact page)" },
  ];
  const save = async () => {
    try {
      const { key, ...body } = s;
      await api.put("/admin/settings", body);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const grabAvatar = async () => {
    const src = s.instagram_url || s.youtube_url || s.linkedin_url;
    if (!src) { toast.error("Add your Instagram link below first"); return; }
    setGrabbing(true);
    try {
      const { data } = await api.post("/admin/resolve-avatar", { url: src });
      const next = { ...s, avatar_url: data.avatar_url };
      setS(next);
      const { key, ...body } = next;
      await api.put("/admin/settings", body);
      toast.success(`Profile photo fetched from ${data.platform}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setGrabbing(false); }
  };
  return (
    <div className="space-y-5">
      <div className="space-y-3">
        {fields.map((k) => (
          <div key={k} className="space-y-2">
            <Field k={k} label={k.replace(/_/g, " ")} value={s[k]} onChange={(kk, v) => setS((p) => ({ ...p, [kk]: v }))} />
            {k === "avatar_url" && (
              <button
                type="button"
                onClick={grabAvatar}
                disabled={grabbing}
                data-testid="fetch-avatar-instagram"
                className="inline-flex items-center gap-1.5 rounded-full border border-[var(--accent)]/40 bg-[var(--accent)]/10 px-3.5 py-2 text-xs font-semibold text-[var(--accent)] disabled:opacity-50"
              >
                {grabbing ? <Loader2 size={13} className="animate-spin" /> : <ImageIcon size={13} />} Auto-fetch from Instagram
              </button>
            )}
          </div>
        ))}
      </div>
      <div className="rounded-2xl border border-[var(--hairline)] bg-white p-4">
        <div className="mb-3">
          <h3 className="text-sm font-bold">Linkit marketing (public site content)</h3>
          <p className="text-xs text-[var(--ink-soft)]">Shown on the Linkit marketing pages (/create, /create/about, /create/contact). Leave founder photo URL empty to reuse your main avatar.</p>
        </div>
        <div className="space-y-3">
          {brandFields.map((bf) => (
            <div key={bf.k} className="space-y-1.5">
              <Label className="text-xs">{bf.label}</Label>
              {bf.k === "founder_bio" ? (
                <Textarea rows={4} value={brand[bf.k] || ""} onChange={(e) => setBrand(bf.k, e.target.value)} data-testid={`brand-${bf.k}`} />
              ) : (
                <Input value={brand[bf.k] || ""} onChange={(e) => setBrand(bf.k, e.target.value)} data-testid={`brand-${bf.k}`} />
              )}
            </div>
          ))}
        </div>
      </div>
      <button onClick={save} data-testid="save-settings" className="w-full rounded-full bg-[var(--accent)] py-3 text-sm font-semibold text-white">
        Save settings
      </button>
    </div>
  );
}

function Appearance() {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/content").then((r) => setS({
      font_family: "Inter",
      background_css: "radial-gradient(circle at 50% 20%, #5a5148 0%, #393631 35%, #1d1d1b 100%)",
      text_primary: "#FFFFFF",
      text_secondary: "rgba(242,242,242,0.68)",
      accent_color: "#C9B79C",
      marquee_enabled: false,
      marquee_text: "MAXIMIZE REWARD POINTS • TRAVEL BETTER • UNLOCK LUXURY • SPEND SMARTER",
      ...r.data.settings,
    }));
  }, []);
  if (!s) return null;
  const upd = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const fonts = ["Inter", "Manrope", "Playfair Display", "Space Grotesk"];
  const presets = [
    { name: "Warm Charcoal", css: "radial-gradient(circle at 50% 20%, #5a5148 0%, #393631 35%, #1d1d1b 100%)", tp: "#FFFFFF" },
    { name: "Deep Black", css: "radial-gradient(circle at 50% 0%, #2a2a2a 0%, #0d0d0d 100%)", tp: "#FFFFFF" },
    { name: "Emerald Night", css: "radial-gradient(circle at 50% 20%, #14312a 0%, #0c1a16 100%)", tp: "#FFFFFF" },
    { name: "Warm Off-White", css: "#F9F8F6", tp: "#111111" },
  ];
  const save = async () => {
    try {
      await api.put("/admin/settings", {
        font_family: s.font_family,
        background_css: s.background_css,
        text_primary: s.text_primary,
        text_secondary: s.text_secondary,
        accent_color: s.accent_color,
        marquee_enabled: s.marquee_enabled,
        marquee_text: s.marquee_text,
      });
      toast.success("Appearance saved — refresh the public site to see changes");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  return (
    <div className="space-y-5">
      <div className="space-y-1.5">
        <Label className="text-xs">Font family</Label>
        <Select value={s.font_family} onValueChange={(v) => upd("font_family", v)}>
          <SelectTrigger data-testid="appearance-font"><SelectValue /></SelectTrigger>
          <SelectContent>
            {fonts.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">Background presets</Label>
        <div className="grid grid-cols-2 gap-2">
          {presets.map((p) => (
            <button
              key={p.name}
              type="button"
              onClick={() => { upd("background_css", p.css); upd("text_primary", p.tp); }}
              className="flex items-center gap-2 rounded-lg border border-black/10 p-2 text-left text-xs font-medium"
            >
              <span className="h-8 w-8 rounded-md border border-black/10" style={{ background: p.css }} />
              {p.name}
            </button>
          ))}
        </div>
        <Textarea rows={2} value={s.background_css} onChange={(e) => upd("background_css", e.target.value)} data-testid="appearance-bg" />
      </div>

      <div className="grid grid-cols-3 gap-3">
        {[["text_primary", "Primary text"], ["text_secondary", "Secondary"], ["accent_color", "Accent"]].map(([k, l]) => (
          <div key={k} className="space-y-1.5">
            <Label className="text-xs">{l}</Label>
            <div className="flex items-center gap-2">
              <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(s[k] || "") ? s[k] : "#ffffff"} onChange={(e) => upd(k, e.target.value)} className="h-9 w-9 rounded border border-black/10" />
              <Input value={s[k] ?? ""} onChange={(e) => upd(k, e.target.value)} />
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-black/10 p-3">
        <div className="flex items-center gap-2">
          <Switch checked={!!s.marquee_enabled} onCheckedChange={(v) => upd("marquee_enabled", v)} data-testid="appearance-marquee-toggle" />
          <Label className="text-xs">Show scrolling marquee</Label>
        </div>
        <Input className="mt-2" placeholder="Separate phrases with •" value={s.marquee_text ?? ""} onChange={(e) => upd("marquee_text", e.target.value)} data-testid="appearance-marquee-text" />
      </div>

      <button onClick={save} data-testid="save-appearance" className="w-full rounded-full bg-[var(--accent)] py-3 text-sm font-semibold text-white">
        Save appearance
      </button>
    </div>
  );
}

function Categories() {
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/admin/categories").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const setField = (id, k, v) => setItems((arr) => arr.map((x) => (x.id === id ? { ...x, [k]: v } : x)));
  const add = async () => { await api.post("/admin/categories", { name: "New Category", order: items.length + 1 }); load(); };
  const save = async (it) => {
    try {
      await api.put(`/admin/categories/${it.id}`, { name: it.name, order: parseInt(it.order, 10) || 0 });
      toast.success("Category saved");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (it) => {
    if (!window.confirm(`Delete heading "${it.name}"? Cards keep their category text.`)) return;
    await api.delete(`/admin/categories/${it.id}`);
    load();
  };
  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Category headings</h2>
          <p className="text-xs text-[var(--ink-soft)]">Rename headings and set their display order. Renaming updates every card in that category.</p>
        </div>
        <button onClick={add} data-testid="add-category" className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-[var(--accent)] px-4 py-2 text-sm font-semibold text-white">
          <Plus size={15} /> Add
        </button>
      </div>
      <div className="space-y-2">
        {items.map((it) => (
          <div key={it.id} className="flex items-center gap-2 rounded-xl border border-[var(--hairline)] bg-white p-2">
            <Input className="w-16" type="number" value={it.order} onChange={(e) => setField(it.id, "order", e.target.value)} data-testid={`cat-order-${it.id}`} />
            <Input value={it.name} onChange={(e) => setField(it.id, "name", e.target.value)} data-testid={`cat-name-${it.id}`} />
            <button onClick={() => save(it)} data-testid={`cat-save-${it.id}`} className="shrink-0 rounded-lg bg-[var(--accent)] px-3 py-2 text-xs font-semibold text-white">Save</button>
            <button onClick={() => del(it)} className="shrink-0 rounded-lg p-2 text-red-600 hover:bg-red-50"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Socials() {
  const [list, setList] = useState(null);
  const [total, setTotal] = useState("");
  const [label, setLabel] = useState("Aggregated following");
  useEffect(() => {
    api.get("/content").then((r) => {
      const s = r.data.settings;
      setList((s.social_links || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0)));
      setTotal(s.followers_total || "");
      setLabel(s.followers_label || "Aggregated following");
    });
  }, []);
  if (!list) return null;
  const upd = (i, k, v) => setList((arr) => arr.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const add = () => setList((arr) => [...arr, { platform: "instagram", label: "", url: "", followers: "", image: "", order: arr.length + 1 }]);
  const del = (i) => setList((arr) => arr.filter((_, idx) => idx !== i));
  const save = async () => {
    try {
      await api.put("/admin/settings", {
        social_links: list.map((s, i) => ({ ...s, order: parseInt(s.order, 10) || i + 1 })),
        followers_total: total,
        followers_label: label,
      });
      toast.success("Socials saved — refresh the public site");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const platforms = ["instagram", "youtube", "linkedin", "x"];
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-[var(--hairline)] bg-white p-3">
        <p className="mb-2 text-xs font-semibold">Aggregated following (shown small under your name)</p>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <Label className="text-xs">Total (leave blank to auto-add)</Label>
            <Input placeholder="e.g. 218K" value={total} onChange={(e) => setTotal(e.target.value)} data-testid="followers-total" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Label text</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} data-testid="followers-label" />
          </div>
        </div>
      </div>

      <p className="text-xs text-[var(--ink-soft)]">Each row appears vertically with its follower count. Add an optional headshot next to the platform logo.</p>
      {list.map((s, i) => (
        <div key={i} className="space-y-2 rounded-xl border border-[var(--hairline)] bg-white p-3">
          <div className="flex gap-2">
            <Select value={s.platform} onValueChange={(v) => upd(i, "platform", v)}>
              <SelectTrigger className="w-32" data-testid={`social-platform-${i}`}><SelectValue /></SelectTrigger>
              <SelectContent>{platforms.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
            <Input placeholder="Handle / label" value={s.label} onChange={(e) => upd(i, "label", e.target.value)} data-testid={`social-label-${i}`} />
            <button onClick={() => del(i)} className="shrink-0 rounded-lg p-2 text-red-600 hover:bg-red-50"><Trash2 size={15} /></button>
          </div>
          <div className="flex gap-2">
            <Input placeholder="Profile URL" value={s.url} onChange={(e) => upd(i, "url", e.target.value)} />
            <Input className="w-32" placeholder="e.g. 120K" value={s.followers} onChange={(e) => upd(i, "followers", e.target.value)} data-testid={`social-followers-${i}`} />
          </div>
          <ImageField k="image" label="Headshot next to logo (optional)" value={s.image} onChange={(_, v) => upd(i, "image", v)} />
        </div>
      ))}
      <button onClick={add} data-testid="add-social" className="inline-flex items-center gap-1.5 rounded-full border border-black/15 px-4 py-2 text-sm font-semibold">
        <Plus size={15} /> Add social
      </button>
      <button onClick={save} data-testid="save-socials" className="w-full rounded-full bg-[var(--accent)] py-3 text-sm font-semibold text-white">
        Save socials
      </button>
    </div>
  );
}

function DroppableColumn({ id, children }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div ref={setNodeRef} className={`min-h-[36px] rounded-lg transition-colors ${isOver ? "bg-[var(--accent)]/5 ring-1 ring-[var(--accent)]/30" : ""}`}>
      {children}
    </div>
  );
}

function DragRow({ id, header, title, subtitle, onEdit, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 20 : undefined,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 rounded-xl border p-2.5 ${header ? "border-transparent bg-[var(--accent)]/10" : "border-[var(--hairline)] bg-white"}`}
    >
      <button
        {...attributes}
        {...listeners}
        aria-label="Drag to reorder"
        data-testid={`drag-${id}`}
        className="cursor-grab touch-none rounded-md p-1 text-black/30 hover:text-black/60 active:cursor-grabbing"
      >
        <GripVertical size={16} />
      </button>
      <div className="min-w-0 flex-1">
        <p className={header ? "truncate text-[11px] font-bold uppercase tracking-wider text-[var(--accent)]" : "truncate text-sm font-semibold"}>
          {title}
        </p>
        {subtitle ? <p className="truncate text-xs text-[var(--ink-soft)]">{subtitle}</p> : null}
      </div>
      <button onClick={onEdit} className="shrink-0 rounded-lg p-2 hover:bg-black/5"><Pencil size={14} /></button>
      <button onClick={onRemove} className="shrink-0 rounded-lg p-2 text-red-600 hover:bg-red-50"><Trash2 size={14} /></button>
    </div>
  );
}

const COLLECTIONS = [
  { key: "wellness", label: "Protein, Wellness & Fitness" },
  { key: "lifestyle", label: "Luggage, Accessories & Lifestyle" },
];
const CARD_EDIT_FIELDS = [
  ["bank_name", "Bank name"],
  ["card_name", "Card name"],
  ["benefit", "One-line benefit"],
  ["label", "Label (optional)"],
  ["cta_text", "Button text"],
  ["cta_url", "Apply / Learn URL"],
  ["fee_note", "Fee note (optional)"],
  ["icon", "Icon key (credit-card, plane, wallet, fuel, gem, crown, smartphone, briefcase, badge-check)"],
  ["image_url", "Card image / logo"],
];
const PRODUCT_EDIT_FIELDS = [
  ["brand", "Brand"],
  ["name", "Product name"],
  ["recommendation", "One-line recommendation"],
  ["image_url", "Product image"],
  ["discount_label", "Discount label"],
  ["shop_url", "Shop URL"],
];

function ContentBuilder() {
  const [cards, setCards] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dialog, setDialog] = useState({ open: false, type: "card", draft: {}, isNew: true });
  const [menuOpen, setMenuOpen] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const load = useCallback(async () => {
    const [c, p, cat] = await Promise.all([
      api.get("/admin/credit-cards"),
      api.get("/admin/products"),
      api.get("/admin/categories"),
    ]);
    setCards(c.data);
    setProducts(p.data);
    setCategories(cat.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const orderedCats = [...categories].sort((a, b) => (a.order || 0) - (b.order || 0));
  const cardsByCat = {};
  orderedCats.forEach((c) => (cardsByCat[c.name] = []));
  const uncategorized = [];
  cards.forEach((cd) => {
    if (cardsByCat[cd.category]) cardsByCat[cd.category].push(cd);
    else uncategorized.push(cd);
  });
  const productsBy = { wellness: [], lifestyle: [] };
  products.forEach((p) => {
    (productsBy[p.collection_key] || (productsBy[p.collection_key] = [])).push(p);
  });

  const endpointFor = (t) => (t === "category" ? "/admin/categories" : t === "card" ? "/admin/credit-cards" : "/admin/products");
  const openAdd = (type) => {
    setMenuOpen(false);
    let draft = { active: true };
    if (type === "category") draft = { name: "", order: categories.length + 1 };
    if (type === "card") draft = { category: orderedCats[0]?.name || "", bank_name: "", card_name: "", benefit: "", label: "", cta_text: "Apply Now", cta_url: "", fee_note: "", icon: "credit-card", image_url: "", active: true, order: cards.length + 1 };
    if (type === "product") draft = { collection_key: "wellness", brand: "", name: "", recommendation: "", image_url: "", discount_label: "Use Code ALY", shop_url: "", active: true, order: products.length + 1 };
    setDialog({ open: true, type, draft, isNew: true });
  };
  const openEdit = (type, item) => setDialog({ open: true, type, draft: { ...item }, isNew: false });
  const change = (k, v) => setDialog((d) => ({ ...d, draft: { ...d.draft, [k]: v } }));
  const save = async () => {
    const { type, draft, isNew } = dialog;
    const payload = { ...draft };
    delete payload._id;
    const id = payload.id;
    delete payload.id;
    try {
      if (isNew) await api.post(endpointFor(type), payload);
      else await api.put(`${endpointFor(type)}/${id}`, payload);
      toast.success("Saved");
      setDialog((d) => ({ ...d, open: false }));
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (type, item) => {
    const name = item.name || item.card_name || item.name;
    if (!window.confirm(`Delete "${item.card_name || item.name}"?`)) return;
    await api.delete(`${endpointFor(type)}/${item.id}`);
    toast.success("Deleted");
    load();
  };

  const isCategory = (id) => categories.some((c) => c.id === id);
  const overCatOf = (list, id) =>
    String(id).startsWith("col:") ? String(id).slice(4) : (list.find((x) => x.id === id)?.category ?? null);
  const overColOf = (list, id) =>
    String(id).startsWith("col:") ? String(id).slice(4) : (list.find((x) => x.id === id)?.collection_key ?? null);

  const persistCards = (arr) => {
    const ids = arr.map((c) => c.id);
    const assignments = {};
    arr.forEach((c) => (assignments[c.id] = c.category));
    api.post("/admin/credit-cards/reorder", { ids, assignments })
      .then(() => toast.success("Saved"))
      .catch(() => { toast.error("Could not save order"); load(); });
  };
  const persistProducts = (arr) => {
    const ids = arr.map((p) => p.id);
    const assignments = {};
    arr.forEach((p) => (assignments[p.id] = p.collection_key));
    api.post("/admin/products/reorder", { ids, assignments })
      .then(() => toast.success("Saved"))
      .catch(() => { toast.error("Could not save order"); load(); });
  };

  const onCategoriesDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldI = orderedCats.findIndex((c) => c.id === active.id);
    let newI = orderedCats.findIndex((c) => c.id === over.id);
    if (newI < 0) {
      const cat = overCatOf(cards, over.id);
      newI = cat ? orderedCats.findIndex((c) => c.name === cat) : -1;
    }
    if (oldI < 0 || newI < 0) return;
    const next = arrayMove(orderedCats, oldI, newI);
    setCategories(next.map((c, i) => ({ ...c, order: i + 1 })));
    api.post("/admin/categories/reorder", { ids: next.map((c) => c.id) })
      .then(() => toast.success("Order saved"))
      .catch(() => { toast.error("Could not save order"); load(); });
  };

  const onCardsDragOver = ({ active, over }) => {
    if (!over || isCategory(active.id)) return;
    setCards((prev) => {
      const activeCard = prev.find((c) => c.id === active.id);
      if (!activeCard) return prev;
      const overCat = overCatOf(prev, over.id);
      if (!overCat || overCat === activeCard.category) return prev;
      const activeIndex = prev.findIndex((c) => c.id === active.id);
      let overIndex;
      if (String(over.id).startsWith("col:")) {
        const idxs = prev.map((c, i) => (c.category === overCat ? i : -1)).filter((i) => i >= 0);
        overIndex = idxs.length ? idxs[idxs.length - 1] + 1 : prev.length;
      } else {
        overIndex = prev.findIndex((c) => c.id === over.id);
        if (overIndex < 0) overIndex = prev.length;
      }
      const arr = [...prev];
      arr.splice(activeIndex, 1);
      const insertAt = overIndex > activeIndex ? overIndex - 1 : overIndex;
      arr.splice(insertAt, 0, { ...activeCard, category: overCat });
      return arr;
    });
  };
  const onCardsDragEnd = (event) => {
    const { active, over } = event;
    if (isCategory(active.id)) return onCategoriesDragEnd(event);
    if (!over) return;
    const overCat = overCatOf(cards, over.id);
    const activeIndex = cards.findIndex((c) => c.id === active.id);
    const overIndex = cards.findIndex((c) => c.id === over.id);
    let arr = cards;
    if (overIndex >= 0 && activeIndex !== overIndex) arr = arrayMove(cards, activeIndex, overIndex);
    if (overCat) arr = arr.map((c) => (c.id === active.id ? { ...c, category: overCat } : c));
    setCards(arr);
    persistCards(arr);
  };

  const onProductsDragOver = ({ active, over }) => {
    if (!over) return;
    setProducts((prev) => {
      const activeProd = prev.find((p) => p.id === active.id);
      if (!activeProd) return prev;
      const overCol = overColOf(prev, over.id);
      if (!overCol || overCol === activeProd.collection_key) return prev;
      const activeIndex = prev.findIndex((p) => p.id === active.id);
      let overIndex;
      if (String(over.id).startsWith("col:")) {
        const idxs = prev.map((p, i) => (p.collection_key === overCol ? i : -1)).filter((i) => i >= 0);
        overIndex = idxs.length ? idxs[idxs.length - 1] + 1 : prev.length;
      } else {
        overIndex = prev.findIndex((p) => p.id === over.id);
        if (overIndex < 0) overIndex = prev.length;
      }
      const arr = [...prev];
      arr.splice(activeIndex, 1);
      const insertAt = overIndex > activeIndex ? overIndex - 1 : overIndex;
      arr.splice(insertAt, 0, { ...activeProd, collection_key: overCol });
      return arr;
    });
  };
  const onProductsDragEnd = (event) => {
    const { active, over } = event;
    if (!over) return;
    const overCol = overColOf(products, over.id);
    const activeIndex = products.findIndex((p) => p.id === active.id);
    const overIndex = products.findIndex((p) => p.id === over.id);
    let arr = products;
    if (overIndex >= 0 && activeIndex !== overIndex) arr = arrayMove(products, activeIndex, overIndex);
    if (overCol) arr = arr.map((p) => (p.id === active.id ? { ...p, collection_key: overCol } : p));
    setProducts(arr);
    persistProducts(arr);
  };

  return (
    <div>
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Content</h2>
          <p className="text-xs text-[var(--ink-soft)]">This mirrors your live page. Drag handles to reorder anything.</p>
        </div>
        <div className="relative shrink-0">
          <button onClick={() => setMenuOpen((o) => !o)} data-testid="content-add" className="inline-flex items-center gap-1.5 rounded-full bg-[var(--accent)] px-4 py-2 text-sm font-semibold text-white">
            <Plus size={15} /> Add
          </button>
          {menuOpen && (
            <div className="absolute right-0 z-30 mt-1 w-48 overflow-hidden rounded-xl border border-[var(--hairline)] bg-white p-1 shadow-xl">
              <button onClick={() => openAdd("category")} data-testid="add-category-opt" className="block w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-black/5">Category heading</button>
              <button onClick={() => openAdd("card")} data-testid="add-card-opt" className="block w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-black/5">Credit card</button>
              <button onClick={() => openAdd("product")} data-testid="add-product-opt" className="block w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-black/5">Product</button>
            </div>
          )}
        </div>
      </div>

      <p className="mb-2 text-[11px] font-bold uppercase tracking-widest text-[var(--ink-soft)]">Best Credit Cards</p>
      <DndContext sensors={sensors} collisionDetection={closestCorners} onDragOver={onCardsDragOver} onDragEnd={onCardsDragEnd}>
        <SortableContext items={orderedCats.map((c) => c.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-4">
            {orderedCats.map((cat) => (
              <div key={cat.id} className="space-y-2">
                <DragRow id={cat.id} header title={cat.name} subtitle={`${cardsByCat[cat.name].length} card(s)`} onEdit={() => openEdit("category", cat)} onRemove={() => del("category", cat)} />
                <DroppableColumn id={`col:${cat.name}`}>
                  <div className="ml-3 border-l-2 border-[var(--hairline)] pl-3">
                    <SortableContext items={cardsByCat[cat.name].map((c) => c.id)} strategy={verticalListSortingStrategy}>
                      <div className="space-y-2 py-1">
                        {cardsByCat[cat.name].map((cd) => (
                          <DragRow key={cd.id} id={cd.id} title={cd.card_name} subtitle={`${cd.bank_name}${cd.label ? " · " + cd.label : ""}`} onEdit={() => openEdit("card", cd)} onRemove={() => del("card", cd)} />
                        ))}
                        {!cardsByCat[cat.name].length && <p className="py-2 text-xs text-[var(--ink-soft)]">Drag a card here.</p>}
                      </div>
                    </SortableContext>
                  </div>
                </DroppableColumn>
              </div>
            ))}
          </div>
        </SortableContext>
      </DndContext>
      {uncategorized.length > 0 && (
        <div className="mt-3 rounded-xl border border-dashed border-[var(--hairline)] p-3">
          <p className="mb-2 text-xs font-semibold text-[var(--ink-soft)]">Uncategorised cards (edit to assign a category)</p>
          <div className="space-y-2">
            {uncategorized.map((cd) => (
              <div key={cd.id} className="flex items-center justify-between rounded-lg bg-white p-2 text-sm">
                <span>{cd.card_name} <span className="text-[var(--ink-soft)]">· {cd.category || "none"}</span></span>
                <button onClick={() => openEdit("card", cd)} className="rounded-lg p-1.5 hover:bg-black/5"><Pencil size={14} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="mb-2 mt-8 text-[11px] font-bold uppercase tracking-widest text-[var(--ink-soft)]">My Favourite Products</p>
      <DndContext sensors={sensors} collisionDetection={closestCorners} onDragOver={onProductsDragOver} onDragEnd={onProductsDragEnd}>
        <div className="space-y-4">
          {COLLECTIONS.map((col) => (
            <div key={col.key} className="space-y-2">
              <div className="rounded-xl bg-[var(--accent)]/10 p-2.5 text-[11px] font-bold uppercase tracking-wider text-[var(--accent)]">{col.label}</div>
              <DroppableColumn id={`col:${col.key}`}>
                <div className="ml-3 border-l-2 border-[var(--hairline)] pl-3">
                  <SortableContext items={(productsBy[col.key] || []).map((p) => p.id)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-2 py-1">
                      {(productsBy[col.key] || []).map((p) => (
                        <DragRow key={p.id} id={p.id} title={p.name} subtitle={p.brand} onEdit={() => openEdit("product", p)} onRemove={() => del("product", p)} />
                      ))}
                      {!(productsBy[col.key] || []).length && <p className="py-2 text-xs text-[var(--ink-soft)]">Drag a product here.</p>}
                    </div>
                  </SortableContext>
                </div>
              </DroppableColumn>
            </div>
          ))}
        </div>
      </DndContext>

      <Dialog open={dialog.open} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}>
        <DialogContent className="max-h-[85vh] overflow-y-auto bg-white text-[#111111]" style={{ "--ink": "#111111", "--ink-soft": "#666666", "--accent": "#0f3d32", "--accent-light": "#eaf0ee", "--hairline": "rgba(17,17,17,0.08)", color: "#111111" }}>
          <DialogHeader>
            <DialogTitle>{dialog.isNew ? "Add" : "Edit"} {dialog.type === "card" ? "credit card" : dialog.type}</DialogTitle>
            <DialogDescription>Fill in the details below, then Save.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {dialog.type === "category" && (
              <Field k="name" label="Category heading" value={dialog.draft.name} onChange={change} />
            )}
            {dialog.type === "card" && (
              <>
                <div className="space-y-1">
                  <Label className="text-xs">Category</Label>
                  <Select value={dialog.draft.category} onValueChange={(v) => change("category", v)}>
                    <SelectTrigger data-testid="card-category-select"><SelectValue placeholder="Choose a category" /></SelectTrigger>
                    <SelectContent>{orderedCats.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {CARD_EDIT_FIELDS.map(([k, label]) => <Field key={k} k={k} label={label} value={dialog.draft[k]} onChange={change} />)}
                <div className="flex items-center gap-2 pt-1">
                  <Switch checked={dialog.draft.active ?? true} onCheckedChange={(v) => change("active", v)} />
                  <Label className="text-xs">Active (visible on site)</Label>
                </div>
              </>
            )}
            {dialog.type === "product" && (
              <>
                <div className="space-y-1">
                  <Label className="text-xs">Collection</Label>
                  <Select value={dialog.draft.collection_key} onValueChange={(v) => change("collection_key", v)}>
                    <SelectTrigger data-testid="product-collection-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{COLLECTIONS.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {PRODUCT_EDIT_FIELDS.map(([k, label]) => <Field key={k} k={k} label={label} value={dialog.draft[k]} onChange={change} />)}
                <div className="flex items-center gap-2 pt-1">
                  <Switch checked={dialog.draft.active ?? true} onCheckedChange={(v) => change("active", v)} />
                  <Label className="text-xs">Active (visible on site)</Label>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <button onClick={save} data-testid="save-item" className="w-full rounded-full bg-[var(--accent)] py-3 text-sm font-semibold text-white">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/auth/login", { email, password });
      localStorage.setItem("tccg_token", res.data.token);
      onLogin(res.data.user);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const sendForgot = async (e) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setForgotBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      setForgotSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setForgotBusy(false); }
  };

  const closeForgot = () => {
    setForgotOpen(false);
    setTimeout(() => { setForgotSent(false); setForgotEmail(""); }, 250);
  };

  return (
    <div className="relative z-[2] flex min-h-screen items-center justify-center px-6" style={LIGHT_THEME}>
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl border border-[var(--hairline)] bg-white p-7 shadow-[0_8px_30px_rgba(0,0,0,0.06)]">
        <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-[var(--accent-light)] text-[var(--accent)]">
          <Lock size={20} />
        </div>
        <h1 className="text-3xl font-bold">Admin access</h1>
        <p className="mb-5 text-sm text-[var(--ink-soft)]">Manage cards, products & content.</p>
        <div className="space-y-3">
          <Input data-testid="admin-email" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <Input data-testid="admin-password" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
          <button type="submit" disabled={loading} data-testid="admin-login" className="w-full rounded-full bg-[var(--accent)] py-3 text-sm font-semibold text-white disabled:opacity-60">
            {loading ? "Signing in…" : "Sign in"}
          </button>
          <button
            type="button"
            onClick={() => { setForgotEmail(email); setForgotOpen(true); }}
            data-testid="admin-forgot"
            className="w-full text-center text-xs font-medium text-[var(--ink-soft)] hover:text-[var(--ink)]"
          >
            Forgot password?
          </button>
        </div>
      </form>

      <Dialog open={forgotOpen} onOpenChange={(o) => (o ? setForgotOpen(true) : closeForgot())}>
        <DialogContent
          className="bg-white text-[#111111]"
          style={{ "--ink": "#111111", "--ink-soft": "#666666", "--accent": "#0f3d32", "--accent-light": "#eaf0ee", "--hairline": "rgba(17,17,17,0.08)", color: "#111111" }}
        >
          <DialogHeader>
            <DialogTitle>{forgotSent ? "Check your inbox" : "Reset your password"}</DialogTitle>
            <DialogDescription>
              {forgotSent
                ? "If that email is registered, a reset link has been sent. It expires in 30 minutes."
                : "Enter your admin email and we'll email you a secure link to set a new password."}
            </DialogDescription>
          </DialogHeader>
          {forgotSent ? (
            <button onClick={closeForgot} data-testid="forgot-close" className="w-full rounded-full bg-[var(--accent)] py-3 text-sm font-semibold text-white">
              Got it
            </button>
          ) : (
            <form onSubmit={sendForgot} className="space-y-3">
              <div className="space-y-1">
                <Label className="text-xs">Admin email</Label>
                <Input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  required
                  data-testid="forgot-email"
                  placeholder="you@example.com"
                />
              </div>
              <button
                type="submit"
                disabled={forgotBusy || !forgotEmail}
                data-testid="forgot-submit"
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[var(--accent)] py-3 text-sm font-semibold text-white disabled:opacity-60"
              >
                {forgotBusy ? <Loader2 size={15} className="animate-spin" /> : null}
                {forgotBusy ? "Sending…" : "Send reset link"}
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [aiOpen, setAiOpen] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const [undoing, setUndoing] = useState(false);

  const checkUndo = useCallback(() => {
    api.get("/admin/ai/undo-status").then((r) => setCanUndo(!!r.data.available)).catch(() => setCanUndo(false));
  }, []);
  useEffect(() => { if (user) checkUndo(); }, [user, checkUndo]);

  const doUndo = async () => {
    if (!window.confirm("Undo the last AI change? This restores your site to just before it.")) return;
    setUndoing(true);
    try {
      await api.post("/admin/ai/undo");
      toast.success("Reverted the last AI change. Reloading…");
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setUndoing(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("tccg_token");
    if (!token) {
      setChecking(false);
      return;
    }
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => localStorage.removeItem("tccg_token")).finally(() => setChecking(false));
  }, []);

  if (checking) return <div className="flex min-h-screen items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-2 border-[var(--hairline)] border-t-[var(--accent)]" /></div>;
  if (!user) return <LoginScreen onLogin={setUser} />;

  const logout = () => {
    localStorage.removeItem("tccg_token");
    setUser(null);
  };

  return (
    <div className="relative z-[2] min-h-screen" style={LIGHT_THEME}>
      <div className="mx-auto w-full max-w-[720px] px-5 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Dashboard</h1>
            <p className="text-sm text-[var(--ink-soft)]">Signed in as {user.email}</p>
          </div>
          <div className="flex items-center gap-2">
            {canUndo && (
              <button onClick={doUndo} disabled={undoing} data-testid="admin-ai-undo" className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-700 disabled:opacity-60">
                {undoing ? <Loader2 size={15} className="animate-spin" /> : <Undo2 size={15} />} Undo AI change
              </button>
            )}
            <button onClick={() => setAiOpen(true)} data-testid="admin-open-ai" className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 px-4 py-2 text-sm font-bold text-white shadow-sm transition-transform hover:scale-105">
              <Sparkles size={15} /> AI Assistant
            </button>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-1.5 rounded-full border border-[var(--hairline)] bg-white px-4 py-2 text-sm font-semibold">
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>

        <AdminAI open={aiOpen} onClose={() => setAiOpen(false)} />

        <Tabs defaultValue="content">
          <TabsList className="mb-5 flex w-full flex-nowrap justify-start gap-1 overflow-x-auto hide-scrollbar">
            <TabsTrigger value="content" data-testid="tab-content">Content</TabsTrigger>
            <TabsTrigger value="socials" data-testid="tab-socials">Socials</TabsTrigger>
            <TabsTrigger value="leads" data-testid="tab-leads">Leads</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
            <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
            <TabsTrigger value="appearance" data-testid="tab-appearance"><Palette size={14} className="mr-1" />Appearance</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
          </TabsList>
          <TabsContent value="content"><UnifiedContent /></TabsContent>
          <TabsContent value="socials"><Socials /></TabsContent>
          <TabsContent value="leads"><Leads /></TabsContent>
          <TabsContent value="analytics"><Analytics /></TabsContent>
          <TabsContent value="activity"><Activity /></TabsContent>
          <TabsContent value="appearance"><Appearance /></TabsContent>
          <TabsContent value="settings"><Settings /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
